<template>
  <div class="form-field-floating lg reactive full form-field-with-prefix" :class="classesNames">
    <select 
      class="form-input-prefix" 
      v-model="formData.indicatif" 
      name="indicatif" 
      id="indicatif"
    >
      <option v-for="country in countries" :key="country.code" :value="country.dial">
        {{ country.dial }}
      </option>
    </select>
    <input 
      type="text" 
      class="form-input" 
      v-model="formData.telephone" 
      name="telephone" 
      :id="inputId"
      :placeholder="placeholder"
    >
    <label :for="inputId">{{ label }} <b class="color-danger" v-if="required">*</b></label>
  </div>
</template>

<script lang="ts">
import { defineComponent, reactive, watch, onMounted } from 'vue'

interface Country {
  name: string
  code: string
  dial: string
}

interface Props {
  modelValue?: string | null
  inputId?: string
  label?: string
  placeholder?: string
  required?: boolean
  classesNames?: string
}

export default defineComponent({
  name: 'CustomPhoneInput',
  props: {
    modelValue: {
      type: String,
      default: null
    },
    inputId: {
      type: String,
      default: 'telephone'
    },
    label: {
      type: String,
      default: 'Numéro de téléphone'
    },
    placeholder: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: true
    },
    classesNames: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue'],
  setup(props: Props, { emit }) {
    const countries: Country[] = [
      { name: 'États-Unis', code: 'US', dial: '+1' },
      { name: 'Canada', code: 'CA', dial: '+1' },
      { name: 'Sénégal', code: 'SN', dial: '+221' },
      { name: 'Mali', code: 'ML', dial: '+223' },
      { name: 'Guinée', code: 'GN', dial: '+224' },
      { name: 'Côte d\'Ivoire', code: 'CI', dial: '+225' },
      { name: 'Burkina Faso', code: 'BF', dial: '+226' },
      { name: 'Niger', code: 'NE', dial: '+227' },
      { name: 'Togo', code: 'TG', dial: '+228' },
      { name: 'Bénin', code: 'BJ', dial: '+229' },
      { name: 'Ghana', code: 'GH', dial: '+233' },
      { name: 'Nigeria', code: 'NG', dial: '+234' },
      { name: 'Rwanda', code: 'RW', dial: '+250' },
      { name: 'Éthiopie', code: 'ET', dial: '+251' },
      { name: 'Kenya', code: 'KE', dial: '+254' },
      { name: 'Tanzanie', code: 'TZ', dial: '+255' },
      { name: 'Afrique du Sud', code: 'ZA', dial: '+27' },
      { name: 'Belgique', code: 'BE', dial: '+32' },
      { name: 'France', code: 'FR', dial: '+33' },
      { name: 'Luxembourg', code: 'LU', dial: '+352' },
      { name: 'Suisse', code: 'CH', dial: '+41' },
      { name: 'Allemagne', code: 'DE', dial: '+49' },
      { name: 'Mexique', code: 'MX', dial: '+52' },
      { name: 'Brésil', code: 'BR', dial: '+55' },
      { name: 'Japon', code: 'JP', dial: '+81' },
      { name: 'Chine', code: 'CN', dial: '+86' },
      { name: 'Inde', code: 'IN', dial: '+91' },
      { name: 'Émirats Arabes Unis', code: 'AE', dial: '+971' },
    ]

    const formData = reactive({
      indicatif: '+225', // Par défaut Côte d'Ivoire
      telephone: null as string | null,
    })

    // Initialiser depuis modelValue si fourni
    onMounted(() => {
      if (props.modelValue) {
        const fullNumber = props.modelValue as string
        for (const country of countries) {
          if (fullNumber.startsWith(country.dial)) {
            formData.indicatif = country.dial
            formData.telephone = fullNumber.slice(country.dial.length)
            break
          }
        }
      }
    })

    // Écouter les changements de modelValue
    watch(() => props.modelValue, (newValue) => {
      if (newValue) {
        const fullNumber = newValue as string
        for (const country of countries) {
          if (fullNumber.startsWith(country.dial)) {
            formData.indicatif = country.dial
            formData.telephone = fullNumber.slice(country.dial.length)
            break
          }
        }
      }
    }, { deep: true })

    // Émettre la valeur concaténée quand formData change
    watch(formData, () => {
      if (formData.telephone && formData.telephone.trim()) {
        const fullNumber = `${formData.indicatif}${formData.telephone.trim()}`
        emit('update:modelValue', fullNumber)
      } else {
        emit('update:modelValue', '')
      }
    }, { deep: true })

    return {
      countries,
      formData
    }
  }
})
</script>

<style scoped>
.form-field-with-prefix {
  position: relative;
}

.form-input-prefix {
  position: absolute;
  left: 0;
  top: 0;
  width: 90px;
  height: 100%;
  font-size: 1rem;
  line-height: 1.5;
  color: var(--component-text, #333);
  font-weight: 500;
  background-color: transparent;
  border: none;
  outline: none;
  padding: 0 12px;
  z-index: 10;
  cursor: pointer;
  border-right: 1px solid rgba(0, 0, 0, 0.1);
}

.form-input-prefix:focus {
  border-color: transparent;
}

.form-field-with-prefix .form-input {
  padding-left: 105px;
}

.form-field-with-prefix label {
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  text-align: center;
}

/* Styles pour le select */
.form-input-prefix {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  padding-right: 28px;
}
</style>
